/**
 * Organization layout - provides org-specific header and sidebar.
 *
 * CRITICAL: This route does NOT call setActiveOrganization().
 * Organization context is explicit from URL only, enabling
 * multiple browser tabs with independent org contexts.
 *
 * Child routes inherit { organization, member, role } via Route.useRouteContext().
 */

import { AppSidebar } from "@/components/layout/app-sidebar";
import { BottomDock } from "@/components/layout/bottom-dock";
import { HeaderProvider } from "@/components/layout/header-provider";
import { SiteHeader } from "@/components/layout/site-header";
import { ThemeProvider } from "@/components/theme-provider";
import {
  organizationBySlugQueryOptions,
  prefetchOrganizationBySlug,
} from "@/lib/queries/organization";
import { SidebarInset, SidebarProvider } from "@repo/ui";
import {
  createFileRoute,
  isRedirect,
  Outlet,
  redirect,
  useMatch,
} from "@tanstack/react-router";

/** User info included with members */
interface MemberUser {
  id: string;
  name: string;
  email: string;
  image?: string | null;
}

/** Member from API (matches Drizzle Member type with optional user) */
export interface OrgMember {
  id: string;
  userId: string;
  organizationId: string;
  role: string;
  createdAt: Date;
  updatedAt: Date;
  user?: MemberUser;
}

export interface OrgRouteContext {
  organization: {
    id: string;
    name: string;
    slug: string;
    logo?: string | null;
    metadata?: string | null; // JSON string from DB
    createdAt: Date;
    updatedAt: Date;
    members: OrgMember[];
  };
  member: OrgMember;
  role: string;
}

export const Route = createFileRoute("/(org)/o/$orgSlug")({
  beforeLoad: async ({ context, params }) => {
    const { orgSlug } = params;

    try {
      const organization = await prefetchOrganizationBySlug(
        context.queryClient,
        orgSlug,
      );

      if (!organization) {
        throw redirect({
          to: "/",
          search: { error: "organization_not_found" },
        });
      }

      // Find current user's membership
      // Note: getBySlug now returns currentUserRole via API if authorized,
      // so we use that as the source of truth.
      const userId = (context as { user?: { id: string } }).user?.id;
      let member: OrgMember | undefined = organization.members.find(
        (m) => m.userId === userId,
      );

      // Delegated Access Handling
      // If user has a role (verified by API) but is not a direct member,
      // create a virtual member object for the UI context.
      if (!member && organization.currentUserRole) {
        if (!userId) {
          throw redirect({
            to: "/",
            search: { error: "unauthorized" },
          });
        }
        member = {
          id: `virtual-${userId}-${organization.id}`,
          userId: userId,
          organizationId: organization.id,
          role: organization.currentUserRole,
          createdAt: new Date(),
          updatedAt: new Date(),
          user: {
            id: userId,
            name: "Delegated User",
            email: "",
          },
        };
      }

      if (!member) {
        throw redirect({
          to: "/",
          search: { error: "not_a_member" },
        });
      }

      return {
        organization,
        member,
        role: member.role,
      } satisfies OrgRouteContext;
    } catch (error) {
      if (isRedirect(error)) throw error; // Re-throw redirects

      // Handle tRPC errors (404, etc.)
      if (
        error instanceof Error &&
        "data" in error &&
        (error as { data?: { code?: string } }).data?.code === "NOT_FOUND"
      ) {
        throw redirect({
          to: "/",
          search: { error: "organization_not_found" },
        });
      }
      // Handle FORBIDDEN
      if (
        error instanceof Error &&
        "data" in error &&
        (error as { data?: { code?: string } }).data?.code === "FORBIDDEN"
      ) {
        throw redirect({
          to: "/",
          search: { error: "organization_not_found" }, // Access denied looks like not found for security
        });
      }

      throw error;
    }
  },
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      organizationBySlugQueryOptions(params.orgSlug),
    );
  },
  component: OrgLayout,
});

function OrgLayout() {
  const isOnboarding = useMatch({
    from: "/(org)/o/$orgSlug/onboarding",
    shouldThrow: false,
  });
  const isSelectRole = useMatch({
    from: "/(org)/o/$orgSlug/select-role",
    shouldThrow: false,
  });
  // The portfolio preview renders the page as visitors see it — no app chrome.
  const isPortfolioPreview = useMatch({
    from: "/(org)/o/$orgSlug/toolkit_/preview",
    shouldThrow: false,
  });

  // The partner connect flow (EPD-11850) is a standalone, chromeless flow.
  const isPartnerConnect = useMatch({
    from: "/(org)/o/$orgSlug/connect",
    shouldThrow: false,
  });

  if (isOnboarding || isSelectRole || isPortfolioPreview || isPartnerConnect) {
    return (
      <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
        <div className="flex min-h-svh flex-1 flex-col font-sans">
          <Outlet />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
      <SidebarProvider>
        <AppSidebar variant="inset" />
        <HeaderProvider>
          <SidebarInset className="min-w-0">
            <SiteHeader />
            <div className="flex flex-1 flex-col font-sans mb-20 md:mb-0 min-w-0">
              <Outlet />
            </div>
          </SidebarInset>
          <BottomDock />
        </HeaderProvider>
      </SidebarProvider>
    </ThemeProvider>
  );
}
